import { Location } from "@/interfaces/location";
import { EventBus } from "@/internal";
import { locationsService } from "@/services/locations.service";
import { messagesService } from "@/services/messages.service";
import { LocationState } from "@/vuex/modules/locations/locations.types";
import { RootState } from "@/vuex/types";
import { TablePagination } from "helix-vue-components";
import { ActionContext, ActionTree } from "vuex";

type LocationManagementActionContext = ActionContext<LocationState, RootState>;

type LocationActionTree = ActionTree<LocationState, RootState>;

export const actions: LocationActionTree = {
  async loadLocations(context: LocationManagementActionContext): Promise<any> {
    try {
      context.commit("setLoading", true);
      const payload: Location[] = await locationsService.getLocations();
      const pagination: TablePagination = await locationsService.getPagination();
      context.commit("setPagination", pagination);
      context.commit("setLocations", payload);
    } catch (e) {
      // error
    } finally {
      context.commit("setLoading", false);
    }
  },
  async saveLocation(
    context: LocationManagementActionContext,
    location: Location
  ): Promise<any> {
    let response = null;
    try {
      context.commit("setLoading", true);
      if (location.id) {
        location._method = "PUT";
        response = await locationsService.put(location, location);
      } else {
        response = await locationsService.postLocation(location);
      }
      EventBus.$emit("notify", { text: "location_saved", color: "success" });
      EventBus.$emit(
        "route",
        { name: "location-management-list" },
        { root: true }
      );
    } catch (e) {
      if (e && e.data) {
        messagesService.renderErrorMessage(e);
      }
    } finally {
      context.commit("setLoading", false);
    }
    return response.data;
  },
  async deleteLocation(
    context: LocationManagementActionContext,
    location: Location
  ): Promise<any> {
    try {
      const response = await locationsService.deleteLocation(location);
      if (response) {
        EventBus.$emit("notify", {
          text: "location_deleted",
          color: "success"
        });

        context.dispatch("AccountManagementModule/loadAvailability", null, {
          root: true
        });

        EventBus.$emit(
          "route",
          { name: "location-management-list" },
          { root: true }
        );
      }
    } catch (e) {
      if (e && e.data) {
        messagesService.renderErrorMessage(e);
      }
    }
  },
  async findLocation(
    context: LocationManagementActionContext,
    dataRequest: {
      id: number;
      route?: string;
    }
  ): Promise<any> {
    try {
      context.commit("setLoading", true);
      const location: Location | null = await locationsService.find(
        Number(dataRequest.id)
      );
      context.commit("setCurrentLocation", location);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  async searchLocation(
    context: LocationManagementActionContext,
    searchQuery: string
  ): Promise<any> {
    try {
      context.commit("setLoading", true);
      const payload: Location[] = await locationsService.searchLocation(
        searchQuery
      );
      context.commit("setLocations", payload);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  }
};
